import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatInputModule } from '@angular/material/input';
import { MaterialModule } from 'src/app/material/material.module';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators, } from '@angular/forms';
import { TextPrettyPipe } from '../../pipes/text-pretty.pipe';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-job-scheduler',
  standalone: true,
  template: `
    <div class="js-head">
      <div class="t">
        <h2>Schedule the time</h2>
        <p>Set when this project runs</p>
      </div>
      <span class="js-x" mat-dialog-close>
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </span>
    </div>

    <mat-dialog-content class="js-body">
      <form [formGroup]="schedulerForm" (ngSubmit)="submit()" class="js-form">
        <div class="fgrid2">
          <mat-form-field class="example-full-width">
            <mat-label>Minute(s):</mat-label>
            <input matInput formControlName="minute"/>
            <mat-hint>for multiple time : 10,15</mat-hint>
          </mat-form-field>

          <mat-form-field class="example-full-width">
            <mat-label>Hour(s):</mat-label>
            <input matInput formControlName="hour"/>
            <mat-hint>for multiple time : 10,15</mat-hint>
          </mat-form-field>
        </div>

        <mat-form-field class="example-full-width">
          <mat-label>Select Day(s) of Month:</mat-label>
          <mat-select
            formControlName="selectedDate"
            multiple
            (selectionChange)="onDateSelectionChange($event)"
          >
            <mat-option value="All">All</mat-option>
            <mat-option
              *ngFor="let option of daysNumbers"
              [value]="option"
              [disabled]="schedulerForm.get('selectedDate').value == 'All'"
            >{{ option }}
            </mat-option
            >
          </mat-select>
        </mat-form-field>

        <mat-form-field class="example-full-width">
          <mat-label>Select month(s) of Year:</mat-label>
          <mat-select
            formControlName="selectedMonth"
            multiple
            (selectionChange)="onMonthSelectionChange($event)"
          >
            <mat-option value="All">All</mat-option>
            <mat-option
              *ngFor="let option of monthsNumbers"
              selected
              [value]="option"
              [disabled]="schedulerForm.get('selectedMonth').value == 'All'"
            >{{ option }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field class="example-full-width">
          <mat-label>Select Day(s) of the week:</mat-label>
          <mat-select
            formControlName="selectedDays"
            multiple
            (selectionChange)="onWeekDaysSelectionChange($event)"
          >
            <mat-option value="All">All</mat-option>
            <mat-option
              *ngFor="let option of daysOfWeek"
              [value]="option"
              [disabled]="schedulerForm.get('selectedDays').value == 'All'"
            >{{ option }}
            </mat-option
            >
          </mat-select>
        </mat-form-field>

        <div class="fgrid2">
          <mat-form-field class="example-full-width">
            <mat-label>Ends On:</mat-label>
            <mat-select formControlName="endOption">
              <mat-option *ngFor="let option of endsOn" [value]="option">{{
                  option | titlecase | textpretty
                }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field
            class="example-full-width"
            *ngIf="schedulerForm.get('endOption').value === 'select_date'"
          >
            <mat-label>Select End Date:</mat-label>
            <input
              matInput
              [matDatepicker]="picker"
              formControlName="expires"
              [min]="minDate"
            />
            <mat-datepicker-toggle
              matIconSuffix
              [for]="picker"
            ></mat-datepicker-toggle>
            <mat-datepicker #picker></mat-datepicker>
            <mat-error *ngIf="schedulerForm.get('expires').invalid"
            >Invalid date
            </mat-error
            >
          </mat-form-field>
        </div>
      </form>
    </mat-dialog-content>

    <mat-dialog-actions class="js-foot">
      <button type="button" class="btn-ghost" mat-dialog-close>No</button>
      <button
        type="button"
        class="btn-primary"
        [disabled]="!schedulerForm.valid"
        (click)="submit()"
      >
        Submit
      </button>
    </mat-dialog-actions>
  `,
  styleUrls: ['./job-scheduler.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MaterialModule,
    MatInputModule,
    TextPrettyPipe,
  ],
})
export class JobSchedulerComponent implements OnInit {
  schedulerForm: FormGroup | any;
  repeatOptions = ['Day', 'Week', 'Month', 'Year'];
  daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  endsOn = ['never', 'select_date'];
  daysNumbers: string[] = [];
  monthsNumbers = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  minDate = new Date();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<JobSchedulerComponent>
  ) {
  }

  ngOnInit(): void {
    this.daysNumbers = Array.from({ length: 31 }, (_, i) => (i + 1).toString());
    this.schedulerForm = this.fb.group({
      minute: ['00'],
      hour: ['00'],
      selectedDate: [null],
      selectedMonth: [null],
      selectedDays: [null],
      expires: [''],
      endOption: ['never', Validators.required],
    });

    if (this.data?.scheduleData) {
      this.patchForm(this.data?.scheduleData);
    }
  }

  patchForm(data: any) {
    const processTimeField = (field: string | string[]): string[] => {
      if (field == null || Array.isArray(field)) {
        return field as string[];
      }
      return field.split(',').map((v: string) => v.trim());
    };
    this.schedulerForm.patchValue({
      minute: data.minute,
      hour: data.hour,
      selectedDate: processTimeField(data.selectedDate),
      selectedMonth: processTimeField(data.selectedMonth),
      selectedDays: processTimeField(data.selectedDays),
      expires: this.formatDate(data.expires),
      endOption: data.endOption,
    });
  }

  formatDate(dateInput: Date | string): string {
    if (!dateInput) return '';
    // Date object from picker: use local components to avoid timezone shift
    // (e.g. selecting 13th showing as 12th in UTC-ahead timezones when using toISOString)
    if (dateInput instanceof Date) {
      const year = dateInput.getFullYear();
      const month = String(dateInput.getMonth() + 1).padStart(2, '0');
      const day = String(dateInput.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    // Date-only string (YYYY-MM-DD): use as-is to avoid timezone parsing issues
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
      return dateInput;
    }
    // ISO string with time: parse and use local date components
    const date = new Date(dateInput);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onChanges(): void {
    this.schedulerForm
      .get('repeatUnit')
      .valueChanges.subscribe((value: any) => {
        const selectedDays = this.schedulerForm.get('selectedDays');
        const selectedDate = this.schedulerForm.get('selectedDate');

        if (value === 'Week') {
          selectedDays.setValidators(Validators.required);
        } else {
          selectedDays.clearValidators();
        }

        if (value === 'Month') {
          selectedDate.setValidators([
            Validators.required,
            Validators.min(1),
            Validators.max(31),
          ]);
        } else {
          selectedDate.clearValidators();
        }

        selectedDays.updateValueAndValidity();
        selectedDate.updateValueAndValidity();
      });

    this.schedulerForm.get('endOption').valueChanges.subscribe((value: any) => {
      const expires = this.schedulerForm.get('expires');
      const occurrences = this.schedulerForm.get('occurrences');

      if (value === 'select_date') {
        expires.setValidators([Validators.required]);
      } else {
        expires.clearValidators();
      }

      expires.updateValueAndValidity();
      occurrences.updateValueAndValidity();
    });
  }

  onDateSelectionChange(event: any) {
    if (event.value.includes('All')) {
      this.schedulerForm.get('selectedDate').setValue(['All']);
    }
  }

  onMonthSelectionChange(event: any) {
    if (event.value.includes('All')) {
      this.schedulerForm.get('selectedMonth').setValue(['All']);
    }
  }

  onWeekDaysSelectionChange(event: any) {
    if (event.value.includes('All')) {
      this.schedulerForm.get('selectedDays').setValue(['All']);
    }
  }

  submit() {
    const formValue = {
      ...this.schedulerForm.value,
    };

    if (this.data?.scheduleData) {
      formValue.id = this.data?.scheduleData?.id;
    }
    const processTimeField = (field: string | string[]): string[] => {
      if (!field) {
        return ['*'];
      }
      if (field == null || Array.isArray(field)) {
        return field as string[];
      }
      return field.split(',').map((v: string) => v.trim());
    };
    formValue.hour = processTimeField(formValue.hour);
    formValue.minute = processTimeField(formValue.minute);
    console.log('submit data', formValue);
    formValue['expires'] = this.formatDate(formValue?.expires);
    this.dialogRef.close(formValue);
  }
}
